/**
 * Small pieces shared by the GovRAMP program page, Today and the FedRAMP
 * Class A page (V103-3-6 / V103-6-3): status glyphs, the date box, ConMon
 * pips, the recorded-fact forms (lapse notice, Ready / Class B booking) and
 * the toast.
 *
 * Toast (design acceptance #2): the toast is shown BEFORE focus moves; while
 * it is visible a `focusin` handler scrolls any focused element clear of its
 * top edge with 16px to spare, so focus is never hidden behind it.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input } from '@aigovops/design-system';
import { invalidationMap } from '../../lib/query-keys';
import { recordProgramEvent, type ProgramEventInput } from '../../lib/govramp-api';
import type { RuleStatus, Tone } from '../../lib/govramp-model';
import '../../pages/govramp.css';

const GLYPH: Record<RuleStatus, string> = { done: '✓', not: '✕', act: '!', noev: '', ns: '' };
/** Decorative status glyph: shape + the adjacent text carry the meaning (never colour alone). */
export function Glyph({ status }: { status: RuleStatus }) {
  return <span className={`gr-g gr-g--${status}`} aria-hidden="true">{GLYPH[status]}</span>;
}

export function Datebox({ label, date, note, late }: { label: string; date: string; note: string; late?: boolean }) {
  return (
    <div className="gr-datebox" data-part="datebox">
      <span className="gr-small gr-dim">{label}</span>
      <b>{date}</b>
      <span className={`gr-small ${late ? 'gr-late' : 'gr-dim'}`}>{note}</span>
    </div>
  );
}

/** ConMon pips + "October ConMon · 2 of 4 done" (the text carries the count; pips are decoration). */
export function Pips({ done, total, tone, label }: { done: number; total: number; tone: Tone; label: string }) {
  return (
    <p className="gr-pipline" data-part="pips">
      <span className="gr-pips" data-tone={tone} aria-hidden="true">{Array.from({ length: total }, (_, i) => <i key={i} className={i < done ? 'on' : undefined} />)}</span>
      {`${label} · ${done} of ${total} done`}
    </p>
  );
}

// ---- toast -------------------------------------------------------------------------------------

export interface ToastSpec { text: string; undo?: () => void | Promise<void>; focusId?: string }

export function useToast(): [ReactNode, (t: ToastSpec) => void] {
  const [toast, setToast] = useState<ToastSpec | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<string | null>(null);
  // Show first, then move focus (the toast is in the layout before focus lands).
  useEffect(() => {
    if (!toast) return;
    const id = pendingFocus.current;
    pendingFocus.current = null;
    if (id) document.getElementById(id)?.focus();
    const t = window.setTimeout(() => setToast(null), 8000);
    return () => window.clearTimeout(t);
  }, [toast]);
  // While visible: whatever takes focus is scrolled above the toast with 16px clearance.
  useEffect(() => {
    if (!toast) return;
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      const box = ref.current?.getBoundingClientRect();
      if (!el || !box || ref.current?.contains(el) || typeof el.getBoundingClientRect !== 'function') return;
      const r = el.getBoundingClientRect();
      const limit = box.top - 16;
      if (r.bottom > limit) window.scrollBy?.({ top: r.bottom - limit, behavior: 'auto' });
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, [toast]);
  const show = useCallback((t: ToastSpec) => { pendingFocus.current = t.focusId ?? null; setToast(t); }, []);
  const node = toast ? (
    <div ref={ref} className="gr-toast" role="status" data-part="toast">
      <span>{toast.text}</span>
      {toast.undo ? <button type="button" onClick={() => { const u = toast.undo!; setToast(null); void u(); }}>Undo</button> : null}
      <button type="button" aria-label="Dismiss" onClick={() => setToast(null)}>✕</button>
    </div>
  ) : null;
  return [node, show];
}

// ---- recorded facts (program events) ------------------------------------------------------------

const today = () => new Date().toISOString().slice(0, 10);

/**
 * "Record Ready booking" / "Record Class B booking": firm + assessment date,
 * one record (posted to `program`, which for a Ready booking is always the
 * GovRAMP program — the FedRAMP page writes the same row).
 */
export function BookingForm({ slug, program, kind, onDone, onCancel, initial, idPrefix }: {
  slug: string; program: string; kind: 'ready_booking' | 'higher_class_booking';
  onDone: (msg: string) => void; onCancel: () => void; initial?: { firm: string; date: string } | null; idPrefix: string;
}) {
  const qc = useQueryClient();
  const [firm, setFirm] = useState(initial?.firm ?? '');
  const [date, setDate] = useState(initial?.date ?? '');
  const [errs, setErrs] = useState<{ firm?: string; date?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: typeof errs = {};
    if (!firm.trim()) next.firm = 'Name the 3PAO firm.';
    if (!date) next.date = 'Choose the assessment date.';
    setErrs(next);
    if (Object.keys(next).length) { document.getElementById(next.firm ? `${idPrefix}-firm` : `${idPrefix}-date`)?.focus(); return; }
    setBusy(true); setError(null);
    try {
      await recordProgramEvent(slug, program, { kind, data: { firm: firm.trim(), assessment_date: date } } as ProgramEventInput);
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      onDone(kind === 'ready_booking' ? `Ready assessment booked · ${firm.trim()}` : `Class B assessment booked · ${firm.trim()}`);
    } catch (err) { setError(err instanceof Error ? err.message : 'The booking could not be recorded.'); }
    finally { setBusy(false); }
  };
  return (
    <form className="gr-recform" onSubmit={submit} noValidate aria-label={kind === 'ready_booking' ? 'Record Ready booking' : 'Record Class B booking'} data-part="booking-form"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id={`${idPrefix}-firm`} label="3PAO firm" required error={errs.firm}>
        <Input value={firm} autoComplete="organization" maxLength={200} onChange={(e) => setFirm(e.currentTarget.value)} />
      </Field>
      <Field id={`${idPrefix}-date`} label="Assessment date" required error={errs.date}>
        <Input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
      </Field>
      <span className="gr-actions">
        <Button type="submit" variant="primary" compact aria-disabled={busy || undefined}>{busy ? 'Recording…' : 'Record'}</Button>
        <Button variant="ghost" compact onClick={onCancel}>Cancel</Button>
      </span>
    </form>
  );
}

/** "Record a lapse notice": the date GovRAMP's notice arrived (never computed) + an optional note. */
export function LapseNoticeForm({ slug, program, onDone, onCancel }: { slug: string; program: string; onDone: (msg: string) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const id = useId().replace(/:/g, '');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!date) { setErr('Choose the notice date.'); document.getElementById(`${id}-date`)?.focus(); return; }
    if (date > today()) { setErr('The notice date can’t be in the future.'); document.getElementById(`${id}-date`)?.focus(); return; }
    setErr(null); setBusy(true); setError(null);
    try {
      await recordProgramEvent(slug, program, { kind: 'govramp_lapse_notice', data: { notice_date: date, ...(note.trim() ? { note: note.trim() } : {}) } });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      onDone('Lapse notice recorded');
    } catch (ex) { setError(ex instanceof Error ? ex.message : 'The notice could not be recorded.'); }
    finally { setBusy(false); }
  };
  return (
    <form className="gr-recform" onSubmit={submit} noValidate aria-label="Record a lapse notice" data-part="lapse-form"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id={`${id}-date`} label="Notice date" required error={err ?? undefined}>
        <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.currentTarget.value)} />
      </Field>
      <Field id={`${id}-note`} label="Note (optional)">
        <Input value={note} maxLength={500} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>
      <span className="gr-actions">
        <Button type="submit" variant="primary" compact aria-disabled={busy || undefined}>{busy ? 'Recording…' : 'Record'}</Button>
        <Button variant="ghost" compact onClick={onCancel}>Cancel</Button>
      </span>
    </form>
  );
}

/** An external link that says it opens a new tab. */
export function ExtLink({ href, children, label }: { href: string; children: ReactNode; label: string }) {
  return <a className="gr-textlink gr-small gr-ext" href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens in a new tab)`}>{children}</a>;
}

/** GovRAMP's list of participating assessors (verified path, design summary). */
export const FIND_3PAO_URL = 'https://govramp.org/program-participants#3paos';
